/**
 * noise-cursor.js
 * Cercle décoratif centré sur la souris, rogné (clip-path) aux bords du
 * conteneur qu'il chevauche parmi ceux listés dans SELECTOR.
 *
 * La détection est géométrique (chevauchement du carré englobant du
 * cercle avec le rect du conteneur), pas basée sur event.target : le
 * cercle apparaît donc dès que son bord s'approche d'un conteneur,
 * avant même que la souris ne soit dessus — seule la portion qui
 * chevauche réellement le conteneur est visible.
 *
 * Détection sur `document` (pas de binding par élément) : fonctionne
 * aussi pour les conteneurs injectés après coup par le routeur JS
 * (contenu remplacé dans #main), sans réinitialisation nécessaire.
 */

const SELECTOR = '.section-header, .page-header, .event-show__header, .post-show__header, .card-title-block, #newsletter'

const RADIUS = 80
const DIAMETER = RADIUS * 2

let circle = null

function ensureCircle() {
  if (!circle) {
    circle = document.createElement('div')
    circle.className = 'noise-cursor'
    document.body.appendChild(circle)
  }
  return circle
}

// Aire d'intersection entre deux rects (0 si disjoints).
function overlapArea(a, b) {
  const width = Math.min(a.right, b.right) - Math.max(a.left, b.left)
  const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
  return width > 0 && height > 0 ? width * height : 0
}

// Parmi les conteneurs chevauchant le carré englobant du cercle, celui
// avec la plus grande aire d'intersection (le plus "sous" le cercle).
function findClosestOverlap(circleBox) {
  let best = null
  let bestArea = 0
  document.querySelectorAll(SELECTOR).forEach(container => {
    const rect = container.getBoundingClientRect()
    const area = overlapArea(circleBox, rect)
    if (area > bestArea) {
      bestArea = area
      best = rect
    }
  })
  return best
}

export function initNoiseCursor() {
  const el = ensureCircle()
  let rafId = null
  // Dernière position connue du curseur (viewport) : un scroll ne bouge pas
  // la souris, seuls les rects des conteneurs changent sous elle, donc on
  // recalcule avec ces coordonnées mises en cache plutôt qu'avec l'event
  // 'scroll' lui-même (qui n'a pas de clientX/clientY).
  let lastClientX = 0
  let lastClientY = 0
  let hasPointer = false

  function scheduleUpdate() {
    if (rafId === null) {
      rafId = requestAnimationFrame(update)
    }
  }

  function update() {
    rafId = null
    if (!hasPointer) return

    const circleLeft = lastClientX - RADIUS
    const circleTop = lastClientY - RADIUS
    el.style.transform = `translate3d(${circleLeft}px, ${circleTop}px, 0)`

    const circleBox = {
      left: circleLeft,
      top: circleTop,
      right: circleLeft + DIAMETER,
      bottom: circleTop + DIAMETER,
    }
    const rect = findClosestOverlap(circleBox)
    el.classList.toggle('is-visible', Boolean(rect))

    if (rect) {
      const top = Math.max(0, rect.top - circleTop)
      const right = Math.max(0, circleLeft + DIAMETER - rect.right)
      const bottom = Math.max(0, circleTop + DIAMETER - rect.bottom)
      const left = Math.max(0, rect.left - circleLeft)
      el.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px)`
    }
  }

  document.addEventListener('pointermove', event => {
    if (event.pointerType && event.pointerType !== 'mouse') return

    lastClientX = event.clientX
    lastClientY = event.clientY
    hasPointer = true
    scheduleUpdate()
  })

  document.addEventListener('pointerleave', () => {
    hasPointer = false
    el.classList.remove('is-visible')
  })

  document.addEventListener('scroll', () => {
    if (!hasPointer) return
    scheduleUpdate()
  }, {passive: true})
}
